import React, { useEffect, useState, useCallback, type ChangeEventHandler } from 'react';
import { boolean, object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { RegistrationFormData } from '@jwp/ott-common/types/account';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { checkConsentsFromValues, extractConsentValues, formatConsentsFromValues } from '@jwp/ott-common/src/utils/collection';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import useOffers from '@jwp/ott-hooks-react/src/useOffers';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';

import RegistrationForm from '../../../components/RegistrationForm/RegistrationForm';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';

const Registration = () => {
  const accountController = getModule(AccountController);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('account');
  const qr = useQueryParam('qr');
  const announce = useAriaAnnouncer();
  const [consentValues, setConsentValues] = useState<Record<string, string | boolean>>({});
  const [consentErrors, setConsentErrors] = useState<string[]>([]);
  const accessModel = useConfigStore((s) => s.accessModel);
  const { mediaOffers } = useOffers();
  const hasMediaOffers = mediaOffers.length > 0;

  const { publisherConsents, publisherConsentsLoading, loading } = useAccountStore(({ publisherConsents, publisherConsentsLoading, loading }) => ({
    publisherConsents,
    publisherConsentsLoading,
    loading,
  }));

  const handleChangeConsent: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ currentTarget }) => {
    if (!currentTarget) return;

    const { name, type } = currentTarget;
    const value = type === 'checkbox' ? (currentTarget as HTMLInputElement).checked : currentTarget.value;

    setConsentValues((current) => ({
      ...current,
      [name]: value,
    }));

    // Clear the errors for any checkbox that's toggled
    setConsentErrors((errors) => errors.filter((e) => e !== name));
  };

  useEffect(() => {
    if (!publisherConsents) {
      accountController.getPublisherConsents();

      return;
    }

    setConsentValues(extractConsentValues(publisherConsents));
  }, [accountController, publisherConsents]);

  const nextStep = useCallback(() => {
    const hasOffers = accessModel === ACCESS_MODEL.SVOD || (accessModel === ACCESS_MODEL.AUTHVOD && hasMediaOffers);

    navigate(modalURLFromLocation(location, hasOffers ? 'choose-offer' : 'welcome'), { replace: true });
  }, [navigate, location, accessModel, hasMediaOffers]);

  const { handleSubmit, handleChange, handleBlur, values, errors, validationSchemaError, submitting } = useForm<RegistrationFormData>({
    initialValues: { email: '', password: '', firstName: '', lastName: '', termsAgreed: '' },
    validationSchema: object().shape({
      email: string()
        .email(t('registration.field_is_not_valid_email'))
        .required(t('registration.field_required', { field: t('registration.email') })),
      password: string()
        .matches(/^(?=.*[a-z])(?=.*[0-9]).{8,}$/, t('registration.invalid_password', { field: t('registration.password') }))
        .required(t('registration.field_required', { field: t('registration.password') })),
      firstName: string()
        .min(2, t('registration.firstNameValidation'))
        .max(50, t('registration.firstNameValidation'))
        .required(t('registration.field_required', { field: t('registration.name') })),
      lastName: string()
        .min(2, t('registration.firstNameValidation'))
        .max(50, t('registration.firstNameValidation'))
        .required(t('registration.field_required', { field: t('registration.name') })),

      termsAgreed: string().oneOf(['true'], t('registration.field_required')).required(t('registration.field_required')),
    }),
    validateOnBlur: true,
    onSubmit: async ({ email, password, firstName, lastName, termsAgreed }) => {
      const { consentsErrors } = checkConsentsFromValues(publisherConsents || [], consentValues);

      if (consentsErrors.length) {
        setConsentErrors(consentsErrors);
        throw new Error(t('registration.consents_error'));
      }

      await accountController.register(
        email,
        password,
        termsAgreed,
        firstName,
        lastName,
        window.location.href,
        formatConsentsFromValues(publisherConsents, consentValues),
        t,
      );
      qr && navigate('/activate');
    },
    onSubmitSuccess: () => {
      announce(t('registration.success'), 'success');
      // navigate(modalURLFromLocation(location, 'personal-details'));
      !qr && nextStep();
    },
    onSubmitError: ({ resetValue }) => resetValue('password'),
  });

  return (
    <RegistrationForm
      onSubmit={handleSubmit}
      onChange={handleChange}
      onConsentChange={handleChangeConsent}
      onBlur={handleBlur}
      values={values}
      errors={errors}
      consentErrors={consentErrors}
      validationError={validationSchemaError}
      submitting={submitting}
      consentValues={consentValues}
      publisherConsents={publisherConsents}
      loading={loading || publisherConsentsLoading}
    />
  );
};

export default Registration;
